import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeSlug === 'all') {
      const allArticles = categories.flatMap((cat) => getArticlesByCategory(cat.slug));
      setArticles(allArticles);
    } else {
      setArticles(getArticlesByCategory(activeSlug));
    }
  }, [activeSlug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    if (newSlug === 'all') {
      navigate('/category/saishi-yuce', { replace: true });
    } else {
      navigate(`/category/${newSlug}`, { replace: true });
    }
  };

  const activeCategory = activeSlug !== 'all' ? getCategoryBySlug(activeSlug) : null;
  const pageTitle = activeCategory ? activeCategory.name : '全部文章';
  const pageDesc = activeCategory ? activeCategory.description : '浏览开运体育全部文章内容';

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${pageTitle} - 开运体育`,
            description: pageDesc,
            url: `https://www.kaiyunsports.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '开运体育',
              url: 'https://www.kaiyunsports.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsports.com/' },
                { '@type': 'ListItem', position: 2, name: pageTitle },
              ],
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ name: '首页', path: '/' }, { name: pageTitle }]} />

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {pageTitle}
                </h1>
                <p className="text-foreground-500 text-sm">{pageDesc}</p>
              </div>

              <CategoryTabs
                categories={categories}
                activeSlug={activeSlug}
                onSelect={handleCategoryChange}
              />

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {articles.length === 0 && (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                  <p className="text-foreground-500 mt-3">暂无相关文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}